// hooks/useSalesData.js
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const API_URL = 'https://demo-api-384317551782.europe-west1.run.app';
// const API_URL = 'http://localhost:3001';

let _socket = null;
const getSocket = () => {
  if (!_socket) {
    _socket = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
  }
  return _socket;
};

const ALL_SENTINELS = new Set([
  'allReps','allCities','allSources','allBranches',
]);

const buildQueryParams = (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo)   params.append('dateTo',   filters.dateTo);

  const dimMap = {
    reps:     'rep',
    cities:   'city',
    sources:  'source',
    branches: 'branch',
  };

  Object.entries(dimMap).forEach(([filterKey, paramKey]) => {
    const values = (filters[filterKey] ?? [])
      .map(item => (typeof item === 'object' ? item.value : item))
      .filter(v => !ALL_SENTINELS.has(v));
    if (values.length > 0) params.append(paramKey, values.join(','));
  });

  return params;
};

export const useSalesData = ({ filters = {} }) => {
  const queryClient = useQueryClient();
  const socketRef   = useRef(null);

  useEffect(() => {
    const socket = getSocket();
    socketRef.current = socket;

    const joinRoom = () => socket.emit('join:sales');
    if (socket.connected) joinRoom();
    socket.on('connect', joinRoom);

    const onSalesUpdated = () => {
      // Only invalidate summary — no sales-data query exists anymore
      queryClient.invalidateQueries({ queryKey: ['sales-summary'] });
      queryClient.invalidateQueries({ queryKey: ['sales-filter-options'] });
    };

    socket.on('sales:updated', onSalesUpdated);

    return () => {
      socket.off('connect',       joinRoom);
      socket.off('sales:updated', onSalesUpdated);
      socket.emit('leave:sales');
    };
  }, [queryClient]);

  // Summary + breakdowns
  const summaryQuery = useQuery({
    queryKey: ['sales-summary', filters],
    queryFn:  async () => {
      const params = buildQueryParams(filters);
      const res    = await fetch(`${API_URL}/api/sales-summary?${params}`);
      if (!res.ok) throw new Error('Failed to fetch summary');
      return res.json();
    },
    staleTime:            Infinity,
    refetchOnWindowFocus: false,
  });

  // Filter options
  const filterOptionsQuery = useQuery({
    queryKey: ['sales-filter-options'],
    queryFn:  async () => {
      const res = await fetch(`${API_URL}/api/filter-options`);
      if (!res.ok) throw new Error('Failed to fetch filter options');
      return res.json();
    },
    staleTime:            Infinity,
    refetchOnWindowFocus: false,
  });

  return {
    summaryStats:   summaryQuery.data   ?? null,
    summaryLoading: summaryQuery.isLoading,
    summaryError:   summaryQuery.isError,
    refetchSummary: summaryQuery.refetch,

    filterOptions:  filterOptionsQuery.data  ?? null,
    optionsLoading: filterOptionsQuery.isLoading,

    isLive: socketRef.current?.connected ?? false,
  };
};